import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import Button from "@mui/material/Button";

// A sankey diagram rendered with d3, for the vintrace batch movement graph.
// The layout is a port of the POC (hacks/vintrace_graph/sankey.go): nodes
// are batches, vessels without a vintrace batch, and the Intake/Packaging
// pseudo-nodes that one-sided movements connect to. They sit in columns
// (longest-path layering, computed by the backend) so every ribbon flows
// strictly left to right; ribbons are sized by volume; a link that closes
// a cycle (back: true) bulges out to the right instead. Node labels carry
// the lot mapping / ferment start for batches and the local id for vessels.
// The diagram is laid out on a fixed canvas (the viewBox) and scaled to
// its container.

export interface SankeyNode {
  id: number;
  kind: string; // "batch", "vessel", "intake" or "packaging"
  name: string;
  sub: string;
  ferm: boolean;
  layer: number;
}

export interface SankeyLink {
  source: number;
  target: number;
  volume: number;
  n: number;
  minDate?: string;
  maxDate?: string;
  back?: boolean;
}

export interface SankeyProps {
  nodes: SankeyNode[];
  links: SankeyLink[];
  title: string;
  subtitle?: string;
  footer?: string;
  unit?: string;
  // when set, an Export button is shown in the top right; it downloads
  // the rendered diagram as `${exportName}.svg`
  exportName?: string;
}

// Layout constants (tuned sparser than the POC, so ribbons and
// unrelated nodes get room instead of overlapping)
const nodeW = 12; // node width in px
const colGap = 220; // horizontal room between columns (back-edge clearance)
const rowGap = 50; // vertical gap between nodes in a column
const minH = 14; // minimum node height in px
const minW = 2; // minimum ribbon width in px
export const ribbonOpacity = 0.2; // ribbon fill opacity
export const ribbonOpacityHover = 0.5; // slightly stronger while hovered

interface LNode {
  node: SankeyNode;
  x: number;
  y: number;
  w: number;
  h: number;
  labelY: number; // vertical centre of the node's label (de-overlapped)
  in: number;
  out: number; // flow through the node
}

interface LLink {
  s: LNode;
  t: LNode;
  volume: number;
  n: number;
  minDate?: string;
  maxDate?: string;
  back: boolean;
  width: number;
  sy0: number;
  sy1: number; // ribbon top/bottom on the source's right edge
  ty0: number;
  ty1: number; // ribbon top/bottom on the target's left edge
}

// One line of the custom hover tooltip (rendered as formatted HTML
// instead of the browser-native <title> tooltip).
interface TipLine {
  text: string;
  strong?: boolean;
  muted?: boolean;
}

interface SankeyTip {
  x: number;
  y: number;
  lines: TipLine[];
}

interface SankeyLayout {
  width: number;
  height: number;
  nodes: LNode[];
  links: LLink[];
}

const flowOf = (n: LNode) => Math.max(n.in, n.out);

// reduceCrossings reorders the nodes within each column to reduce ribbon
// crossings: a few barycenter passes over the columns, each node sorting
// toward the average rank of the nodes it connects to in the adjacent
// columns. Nodes without such neighbours keep their flow-descending rank.
const reduceCrossings = (
  colIDs: number[],
  cols: Map<number, LNode[]>,
  links: LLink[],
) => {
  for (const c of colIDs) {
    const col = cols.get(c)!;
    col.sort((a, b) => {
      if (flowOf(a) !== flowOf(b)) {
        return flowOf(b) - flowOf(a);
      }
      return a.node.name.localeCompare(b.node.name);
    });
  }
  const rank = (c: number) => {
    const m = new Map<LNode, number>();
    cols.get(c)!.forEach((n, i) => m.set(n, i));
    return m;
  };
  for (let pass = 0; pass < 4; pass++) {
    for (let i = 0; i < colIDs.length; i++) {
      const ci = pass % 2 === 1 ? colIDs.length - 1 - i : i;
      const c = colIDs[ci];
      const left = ci > 0 ? rank(colIDs[ci - 1]) : undefined;
      const right = ci < colIDs.length - 1 ? rank(colIDs[ci + 1]) : undefined;
      const slots: { n: LNode; pos: number }[] = [];
      cols.get(c)!.forEach((n, j) => {
        let sum = 0;
        let cnt = 0;
        for (const l of links) {
          let nb: LNode | undefined;
          let r: Map<LNode, number> | undefined;
          if (l.s === n) {
            nb = l.t;
            r = right;
          } else if (l.t === n) {
            nb = l.s;
            r = left;
          } else {
            continue;
          }
          if (nb && r && r.has(nb)) {
            sum += r.get(nb)!;
            cnt++;
          }
        }
        slots.push({ n, pos: cnt > 0 ? sum / cnt : j });
      });
      slots.sort((a, b) => a.pos - b.pos);
      slots.forEach((s, j) => (cols.get(c)![j] = s.n));
    }
  }
};

// assignAnchors stacks each node's outgoing ribbons (right edge) and
// incoming ribbons (left edge) in the other node's vertical order, sizing
// them by volume. The anchors depend on the node positions, so this runs
// once per relaxation sweep and once more for the final positions.
const assignAnchors = (nodes: LNode[], links: LLink[], scale: number) => {
  const outLinks = new Map<LNode, LLink[]>();
  const inLinks = new Map<LNode, LLink[]>();
  for (const l of links) {
    const out = outLinks.get(l.s);
    if (out) out.push(l);
    else outLinks.set(l.s, [l]);
    const inc = inLinks.get(l.t);
    if (inc) inc.push(l);
    else inLinks.set(l.t, [l]);
  }
  const assign = (
    n: LNode,
    ls: LLink[],
    other: (l: LLink) => LNode,
    isOut: boolean,
  ) => {
    ls.sort((a, b) => {
      const oa = other(a);
      const ob = other(b);
      if (oa.y !== ob.y) {
        return oa.y - ob.y;
      }
      return oa.node.name.localeCompare(ob.node.name);
    });
    let sum = 0;
    for (const l of ls) {
      let w = l.volume * scale;
      if (w < minW) {
        w = minW;
      }
      l.width = w;
      sum += w;
    }
    if (sum > n.h && sum > 0) {
      // ribbons don't fit under the node; squeeze
      for (const l of ls) {
        l.width *= n.h / sum;
      }
    }
    let y = n.y;
    for (const l of ls) {
      if (isOut) {
        l.sy0 = y;
        l.sy1 = y + l.width;
      } else {
        l.ty0 = y;
        l.ty1 = y + l.width;
      }
      y += l.width;
    }
  };
  for (const n of nodes) {
    assign(n, outLinks.get(n) ?? [], (l) => l.t, true);
    assign(n, inLinks.get(n) ?? [], (l) => l.s, false);
  }
};

// relaxColumns refines the initial vertical placement (each column packed
// and centered as a block). Alternating sweeps align the flow: a
// left-to-right sweep pulls each node toward the slots its incoming
// ribbons leave at, a right-to-left sweep toward the slots its outgoing
// ribbons arrive at (volume-weighted). Thick ribbons end up running in
// their own parallel bands instead of stacked on one line. A collision
// pass keeps the column's order, row gaps and canvas bounds.
const relaxColumns = (
  nodes: LNode[],
  colIDs: number[],
  cols: Map<number, LNode[]>,
  links: LLink[],
  scale: number,
  top: number,
  bottom: number,
) => {
  const inLinks = new Map<LNode, LLink[]>();
  const outLinks = new Map<LNode, LLink[]>();
  for (const l of links) {
    const out = outLinks.get(l.s);
    if (out) out.push(l);
    else outLinks.set(l.s, [l]);
    const inc = inLinks.get(l.t);
    if (inc) inc.push(l);
    else inLinks.set(l.t, [l]);
  }
  for (let i = 0; i < 40; i++) {
    assignAnchors(nodes, links, scale); // anchors of the current positions
    const leftToRight = i % 2 === 0;
    let moved = 0;
    for (let ci = 0; ci < colIDs.length; ci++) {
      const c = leftToRight ? colIDs[ci] : colIDs[colIDs.length - 1 - ci];
      const col = cols.get(c)!;
      // desired shift per node: the volume-weighted mean of how far the
      // sweep-side ribbon slots are from the node's own slots
      const shift = new Array(col.length).fill(0);
      for (let j = 0; j < col.length; j++) {
        const n = col[j];
        const ls = (leftToRight ? inLinks : outLinks).get(n) ?? [];
        let sum = 0;
        let wsum = 0;
        for (const l of ls) {
          let a: number;
          let b: number;
          if (leftToRight) {
            a = (l.sy0 + l.sy1) / 2;
            b = (l.ty0 + l.ty1) / 2;
          } else {
            a = (l.ty0 + l.ty1) / 2;
            b = (l.sy0 + l.sy1) / 2;
          }
          sum += (a - b) * l.volume;
          wsum += l.volume;
        }
        if (wsum > 0) {
          shift[j] = sum / wsum;
        }
      }
      // apply, keeping the column's order and row gaps
      let y = 0;
      for (let j = 0; j < col.length; j++) {
        const n = col[j];
        y = n.y + shift[j];
        if (y < top) {
          y = top;
        }
        if (j > 0 && y < col[j - 1].y + col[j - 1].h + rowGap) {
          y = col[j - 1].y + col[j - 1].h + rowGap;
        }
        const d = Math.abs(y - n.y);
        if (d > moved) {
          moved = d;
        }
        n.y = y;
      }
      const first = col[0].y;
      const last = col[col.length - 1].y + col[col.length - 1].h;
      if (last - first > bottom - top) {
        // the desired centres span more than the canvas; pack the column
        // with its minimum span, centered
        let total = 0;
        for (const n of col) {
          total += n.h;
        }
        total += rowGap * (col.length - 1);
        y = top + (bottom - top - total) / 2;
        for (const n of col) {
          n.y = y;
          y += n.h + rowGap;
        }
        moved = 1;
      } else {
        // the span fits: slide the stack into the canvas
        if (last - bottom > 0) {
          for (const n of col) {
            n.y -= last - bottom;
          }
        }
        if (top - col[0].y > 0) {
          for (const n of col) {
            n.y += top - col[0].y;
          }
        }
      }
    }
    if (moved < 0.1) {
      break; // settled
    }
  }
  assignAnchors(nodes, links, scale); // anchors of the final positions
};

// ribbonPath renders the link as a filled ribbon between the source's
// right edge and the target's left edge. The layering puts every source in
// a left column of its target; the one exception is a cycle-closing back
// edge, which bulges out to the right instead of crossing the columns.
const ribbonPath = (l: LLink): string => {
  const x0 = l.s.x + l.s.w;
  const x1 = l.t.x;
  let c0: number;
  let c1: number;
  if (x1 > x0) {
    const mid = x0 + (x1 - x0) / 2;
    c0 = mid;
    c1 = mid;
  } else {
    const bulge = 40 + (x0 - x1) * 0.3;
    c0 = x0 + bulge;
    c1 = x1 + bulge;
  }
  const f = (v: number) => v.toFixed(1);
  return `M${f(x0)} ${f(l.sy0)} C${f(c0)} ${f(l.sy0)} ${f(c1)} ${f(l.ty0)} ${f(x1)} ${f(
    l.ty0,
  )} L${f(x1)} ${f(l.ty1)} C${f(c1)} ${f(l.ty1)} ${f(c0)} ${f(l.sy1)} ${f(
    x0,
  )} ${f(l.sy1)} Z`;
};

const layoutSankey = (
  nodes: SankeyNode[],
  links: SankeyLink[],
): SankeyLayout => {
  if (nodes.length === 0) {
    return { width: 1100, height: 420, nodes: [], links: [] };
  }

  // deterministic node order: the layout passes keep it
  const sortedNodes = [...nodes].sort(
    (a, b) => a.layer - b.layer || a.id - b.id,
  );
  const nodeByID = new Map<number, LNode>();
  const lnodes: LNode[] = sortedNodes.map((node) => {
    const ln: LNode = {
      node,
      x: 0,
      y: 0,
      w: nodeW,
      h: minH,
      labelY: 0,
      in: 0,
      out: 0,
    };
    nodeByID.set(node.id, ln);
    return ln;
  });
  const llinks: LLink[] = links
    .map((l) => ({
      s: nodeByID.get(l.source)!,
      t: nodeByID.get(l.target)!,
      volume: l.volume,
      n: l.n,
      minDate: l.minDate,
      maxDate: l.maxDate,
      back: !!l.back,
      width: 0,
      sy0: 0,
      sy1: 0,
      ty0: 0,
      ty1: 0,
    }))
    .filter((l) => l.s && l.t);
  for (const l of llinks) {
    l.s.out += l.volume;
    l.t.in += l.volume;
  }
  // deterministic link order: it affects the draw order of overlapping
  // ribbons and every accumulation
  llinks.sort(
    (a, b) =>
      a.s.node.layer - b.s.node.layer ||
      a.s.node.name.localeCompare(b.s.node.name) ||
      a.t.node.layer - b.t.node.layer ||
      a.t.node.name.localeCompare(b.t.node.name),
  );

  const cols = new Map<number, LNode[]>();
  for (const n of lnodes) {
    const col = cols.get(n.node.layer);
    if (col) {
      col.push(n);
    } else {
      cols.set(n.node.layer, [n]);
    }
  }
  const colIDs = [...cols.keys()].sort((a, b) => a - b);
  reduceCrossings(colIDs, cols, llinks);

  // canvas size from the graph's shape. The labels sit to the right of
  // every node (last column included), so the right margin has to fit the
  // widest one (~8px per char at the 13px name size, ~6.5px at the 10px
  // sub-label size)
  const maxPerCol = Math.max(...colIDs.map((c) => cols.get(c)!.length));
  const labelW = Math.max(
    0,
    ...lnodes.map((n) =>
      Math.max(8 * n.node.name.length, 6.5 * n.node.sub.length),
    ),
  );
  const width = Math.max(
    1100, // the legend row needs at least this much
    50 + (colIDs.length - 1) * (nodeW + colGap) + nodeW + labelW + 24,
  );
  const height = Math.min(2000, Math.max(420, 104 + maxPerCol * 84 + 56));
  const topPad = 104;
  const bottomPad = 56;

  // the vertical scale of the most crowded column, so every column fits
  const availH = height - topPad - bottomPad;
  let scale = 1e9;
  for (const c of colIDs) {
    const col = cols.get(c)!;
    const sum = col.reduce((s, n) => s + flowOf(n), 0);
    if (sum > 0) {
      const s = (availH - rowGap * (col.length - 1)) / sum;
      if (s < scale) {
        scale = s;
      }
    }
  }
  if (scale > 1e8) {
    scale = 1; // no flows at all; minimum heights only
  }

  const xOf = (c: number) => 50 + (c - colIDs[0]) * (nodeW + colGap);
  for (const c of colIDs) {
    const col = cols.get(c)!;
    let total = 0;
    for (const n of col) {
      n.w = nodeW;
      n.h = minH;
      if (scale * flowOf(n) > n.h) {
        n.h = scale * flowOf(n);
      }
      total += n.h;
    }
    total += rowGap * (col.length - 1);
    if (total > availH) {
      // minimum heights overflowed; shrink to fit
      for (const n of col) {
        n.h *= availH / total;
      }
      total = availH;
    }
    let y = topPad + (availH - total) / 2;
    for (const n of col) {
      n.x = xOf(c);
      n.y = y;
      y += n.h + rowGap;
    }
  }

  relaxColumns(lnodes, colIDs, cols, llinks, scale, topPad, height - bottomPad);

  // label positions: one 26px slot per node, in column order, so the name
  // and sub labels of neighbouring nodes never overlap (labels may drift
  // away from short nodes)
  for (const c of colIDs) {
    let last = 0;
    for (const n of cols.get(c)!) {
      let cy = n.y + n.h / 2;
      if (cy < last + 26) {
        cy = last + 26;
      }
      const max = height - bottomPad - 16;
      if (cy > max) {
        cy = max;
      }
      n.labelY = cy;
      last = cy;
    }
  }

  return { width, height, nodes: lnodes, links: llinks };
};

export const Sankey = ({
  nodes,
  links,
  title,
  subtitle,
  footer,
  unit = "L",
  exportName,
}: SankeyProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const layout = useMemo(() => layoutSankey(nodes, links), [nodes, links]);
  const [tip, setTip] = useState<SankeyTip | null>(null);

  // The custom hover tooltip follows the cursor inside the wrapper, and
  // flips to the left of the cursor near the right edge.
  const showTip = (clientX: number, clientY: number, lines: TipLine[]) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    let x = clientX - rect.left + 14;
    const y = clientY - rect.top + 14;
    if (x + 280 > rect.width) x = clientX - rect.left - 294;
    setTip({ x, y, lines });
  };
  const hideTip = () => setTip(null);

  // Serialize the rendered diagram as a standalone .svg file and download
  // it (same blob + anchor pattern as the CSV exports). The in-page svg
  // scales via CSS, so the exported clone gets explicit width/height.
  const downloadSvg = () => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("width", String(layout.width));
    clone.setAttribute("height", String(layout.height));
    clone.removeAttribute("style");
    clone.setAttribute("font-family", "Helvetica, Arial, sans-serif");
    // The in-page tooltip is rendered as HTML, so for the exported file
    // restore SVG-native <title> tooltips from the data-tip attributes.
    clone.querySelectorAll("[data-tip]").forEach((el) => {
      const t = document.createElementNS("http://www.w3.org/2000/svg", "title");
      t.textContent = el.getAttribute("data-tip") ?? "";
      el.insertBefore(t, el.firstChild);
    });
    const blob = new Blob(
      [
        '<?xml version="1.0" encoding="UTF-8"?>\n',
        new XMLSerializer().serializeToString(clone),
      ],
      { type: "image/svg+xml;charset=utf-8" },
    );
    const file = window.URL.createObjectURL(blob);
    const name = (exportName || "sankey").replace(/[^\w.-]+/g, "-");
    const a = document.createElement("a");
    a.href = file;
    a.download = `${name}.svg`;
    document.body.appendChild(a); // must be in the DOM -> otherwise it won't work in firefox
    a.click();
    a.remove();
  };

  useEffect(() => {
    if (!svgRef.current) {
      return;
    }
    const canvas = d3.select(svgRef.current);
    canvas.selectAll("*").remove();

    const { height, nodes: lnodes, links: llinks } = layout;

    const fmtDate = (s?: string) =>
      s ? new Date(s).toISOString().slice(0, 10) : "-";
    const fmtVol = (v: number, u: string) => `${Math.round(v)} ${u || "L"}`;

    // Tooltip content: ribbonTip is the formatted HTML lines, ribbonTipText
    // the flat one-liner stored in data-tip for the SVG export.
    const ribbonTip = (l: LLink): TipLine[] => [
      { text: `${l.s.node.name} \u2192 ${l.t.node.name}`, strong: true },
      {
        text: `${fmtVol(l.volume, unit)} over ${l.n} movement${
          l.n === 1 ? "" : "s"
        }`,
      },
      ...(l.minDate || l.maxDate
        ? [
            {
              text: `${fmtDate(l.minDate)} .. ${fmtDate(l.maxDate)}`,
              muted: true,
            },
          ]
        : []),
    ];
    const ribbonTipText = (l: LLink) => {
      const dates =
        l.minDate || l.maxDate
          ? `, ${fmtDate(l.minDate)} .. ${fmtDate(l.maxDate)}`
          : "";
      return `${ribbonTip(l)[0].text}: ${ribbonTip(l)[1].text}${dates}`;
    };
    const nodeTip = (n: LNode): TipLine[] => [
      { text: n.node.name, strong: true },
      ...(n.node.sub ? [{ text: n.node.sub, muted: true }] : []),
    ];

    canvas
      .append("rect")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("fill", "#ffffff");

    canvas
      .append("text")
      .attr("x", 24)
      .attr("y", 34)
      .attr("font-size", 17)
      .attr("font-weight", "bold")
      .attr("fill", "#1c1917")
      .text(title);

    if (subtitle) {
      canvas
        .append("text")
        .attr("x", 24)
        .attr("y", 54)
        .attr("font-size", 12)
        .attr("fill", "#57534e")
        .text(subtitle);
    }

    // legend
    const legendY = 74;
    const legend = [
      {
        x: 24,
        fill: "#7d3140",
        opacity: 1,
        txt: "batch, fermentation started (StartFerment)",
      },
      {
        x: 330,
        fill: "#8b93a1",
        opacity: 1,
        txt: "batch, no StartFerment in stored data",
      },
      {
        x: 640,
        fill: "#5b7c9d",
        opacity: 1,
        txt: "vessel / intake / packaging (no vintrace batch)",
      },
      { x: 940, fill: "#7d3140", opacity: 0.3, txt: "ribbon width = volume" },
    ];
    for (const item of legend) {
      canvas
        .append("rect")
        .attr("x", item.x)
        .attr("y", legendY - 10)
        .attr("width", 12)
        .attr("height", 12)
        .attr("rx", 2)
        .attr("fill", item.fill)
        .attr("opacity", item.opacity)
        .attr("stroke", "#57534e")
        .attr("stroke-width", 0.5);
      canvas
        .append("text")
        .attr("x", item.x + 17)
        .attr("y", legendY)
        .attr("font-size", 11)
        .attr("fill", "#57534e")
        .text(item.txt);
    }

    // ribbons first, so nodes and labels sit on top
    const linkPaths = canvas
      .append("g")
      .selectAll("path")
      .data(llinks)
      .join("path")
      .attr("d", ribbonPath)
      .attr("fill", "#7d3140")
      .attr("opacity", ribbonOpacity);
    linkPaths
      .attr("data-tip", (l: LLink) => ribbonTipText(l))
      .attr(
        "data-testid",
        (l: LLink) => `vintrace-ribbon-${l.s.node.id}-${l.t.node.id}`,
      )
      .on("mousemove", (event: any, l: LLink) => {
        // emphasize the hovered ribbon (restored on mouseleave)
        event.currentTarget.setAttribute("opacity", String(ribbonOpacityHover));
        showTip(event.clientX, event.clientY, ribbonTip(l));
      })
      .on("mouseleave", (event: any) => {
        event.currentTarget.setAttribute("opacity", String(ribbonOpacity));
        hideTip();
      });

    for (const n of lnodes) {
      let fill = "#8b93a1";
      let stroke = "#5f6673";
      if (n.node.kind !== "batch") {
        fill = "#5b7c9d";
        stroke = "#3d5a7a";
      } else if (n.node.ferm) {
        fill = "#7d3140";
        stroke = "#521f28";
      }

      const node = canvas.append("g");
      node
        .attr(
          "data-tip",
          n.node.sub ? `${n.node.name} (${n.node.sub})` : n.node.name,
        )
        .on("mousemove", (event: any) =>
          showTip(event.clientX, event.clientY, nodeTip(n)),
        )
        .on("mouseleave", hideTip);
      node
        .append("rect")
        .attr("x", n.x.toFixed(1))
        .attr("y", n.y.toFixed(1))
        .attr("width", n.w.toFixed(1))
        .attr("height", n.h.toFixed(1))
        .attr("rx", 3)
        .attr("fill", fill)
        .attr("stroke", stroke);
      // labels always on the right of the node (last column included)
      const lx = n.x + n.w + 7;
      node
        .append("text")
        .attr("x", lx.toFixed(1))
        .attr("y", (n.labelY - 1).toFixed(1))
        .attr("font-size", 13)
        .attr("fill", "#292524")
        .attr("text-anchor", "start")
        .text(n.node.name);
      node
        .append("text")
        .attr("x", lx.toFixed(1))
        .attr("y", (n.labelY + 12).toFixed(1))
        .attr("font-size", 10)
        .attr("fill", "#78716c")
        .attr("text-anchor", "start")
        .text(n.node.sub);
    }

    if (footer) {
      canvas
        .append("text")
        .attr("x", 24)
        .attr("y", height - 24)
        .attr("font-size", 11)
        .attr("fill", "#78716c")
        .text(footer);
    }
  }, [layout, title, subtitle, footer, unit]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      {exportName !== undefined && (
        <Button
          size="small"
          variant="outlined"
          onClick={downloadSvg}
          style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
        >
          Export
        </Button>
      )}
      <div style={{ overflowX: "auto" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          style={{
            // the full geometry lives in the viewBox; the width/height cap the
            // display size so the whole diagram scales onto one page
            width: "100%",
            minWidth: 900,
            maxWidth: 1700,
            height: "auto",
            display: "block",
            fontFamily: "Helvetica, Arial, sans-serif",
          }}
        />
      </div>
      {tip && (
        <div
          style={{
            position: "absolute",
            left: tip.x,
            top: tip.y,
            zIndex: 2,
            pointerEvents: "none",
            background: "#1c1917",
            color: "#fafaf9",
            borderRadius: 4,
            padding: "6px 10px",
            fontSize: 12,
            lineHeight: 1.45,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35)",
          }}
        >
          {tip.lines.map((l, i) => (
            <div
              key={i}
              style={{
                fontWeight: l.strong ? 600 : 400,
                color: l.muted ? "#a8a29e" : "#fafaf9",
              }}
            >
              {l.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
